import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { toListQuery } from './filters';
import {
  installationDetailSchema,
  installationListResponseSchema,
  installerOptionSchema,
} from './schemas';
import type {
  ApproveInstallationBody,
  InstallationDetail,
  InstallationListParams,
  InstallationListResponse,
  InstallerOption,
  RejectInstallationValues,
} from './types';

/**
 * Columns whose `dataIndex` is not the backend's own `sortBy` token.
 *
 * `<DataTable>` puts antd's `SorterResult.field` — i.e. the column's `dataIndex` — straight on the
 * wire, so every other column here is named for its token by coincidence of it matching. Two
 * cannot be: Customer Name renders `customerName` where the enum says `customer`, and No of
 * Devices renders `selectedDeviceCount` where the enum says `deviceCount`. Sending the former of
 * each 400'd with `VALIDATION_FAILED` on every click of those two headers (ADO 119375).
 *
 * Translated here rather than by renaming either `dataIndex`, so a column keeps pointing at the
 * value it displays and the wire token stays a property of the request — the same shape
 * `replacements/api.ts` took for ADO 118732.
 */
const SORT_BY_ALIASES: Record<string, string> = {
  customerName: 'customer',
  selectedDeviceCount: 'deviceCount',
};

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function installationsRequestQuery(params: Omit<InstallationListParams, 'page' | 'limit'>) {
  const { sortBy = 'orderDate', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy: SORT_BY_ALIASES[sortBy] ?? sortBy,
    sortOrder: sortOrder.toUpperCase(),
    ...toListQuery(filters),
  };
}

/** Paginated/sorted/filtered/searchable installation-request list (one row per order).
 *  Defaults to most-recent Order Date first (real backend has no `orderedAt`; the closest —
 *  and default — sort column is `orderDate`). Real backend: `POST .../query` with a JSON body. */
export async function listInstallations(
  params: InstallationListParams,
): Promise<InstallationListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.postList(endpoints.requests.query, {
    page,
    limit,
    ...installationsRequestQuery(rest),
  });
  return installationListResponseSchema.parse({ data, meta });
}

/** Active field-installer directory (`/requests/admin/meta/field-installers`): flat, unpaginated,
 *  optional `search`. Serves both the filter drawer's Assignee options (no search) and the
 *  approve-modal installer typeahead (search as typed). */
export async function listFieldInstallers(search?: string): Promise<InstallerOption[]> {
  return z.array(installerOptionSchema).parse(
    await apiClient.get(endpoints.requests.metaFieldInstallers, {
      params: search ? { search } : undefined,
    }),
  );
}

/** Full installation-request detail (adds structured address + enriched devices +
 *  rejectionReason). Keyed by `orderId` — the real backend has no separate installation-request
 *  id (one row per order). */
export async function getInstallation(orderId: string): Promise<InstallationDetail> {
  return installationDetailSchema.parse(await apiClient.get(endpoints.requests.detail(orderId)));
}

/** Rejects an installation request with an admin-entered reason. Releases the held
 *  installation fee server-side (CONTEXT.md). Real backend: `PATCH`, and returns only
 *  `{orderId, status}` — the caller refetches the full detail rather than seeding it. */
export async function rejectInstallation(
  orderId: string,
  values: RejectInstallationValues,
): Promise<void> {
  await apiClient.patch(endpoints.requests.reject(orderId), values);
}

/** Approves an installation request, assigning either a seeded internal installer or a
 *  free-text external one. Captures the held installation fee server-side. Real backend:
 *  `PATCH`, returning a slim `{orderId, status, ...}` union (success vs. capture-failure) —
 *  the caller reads `status`/`captureFailure` off the raw result and refetches the full detail. */
export async function approveInstallation(
  orderId: string,
  body: ApproveInstallationBody,
): Promise<{ status: string; captureFailure?: boolean; rejectionReason?: string }> {
  return apiClient.patch(endpoints.requests.approve(orderId), body);
}
